import { FC, useMemo } from "react"
import { useMutation, useQueryClient } from "react-query"

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import DeleteIcon from "@mui/icons-material/DeleteOutline"

import { Button, InputText, NotificationUtils } from "@synapse-analytics/synapse-ui"
import { useFormik } from "formik"
import * as Yup from "yup"

import { VisionAPI } from "../../../../API/VisionAPI"
import BranchesAutocomplete from "../../../../components/BranchesAutocomplete"
import VisionDialog from "../../../../components/VisionDialog"
import { definitions } from "../../../../types/Generated/apiTypes"

import styles from "./AddNewCollectionDialog.module.scss"

type Branch = definitions["Branch"]

type Collection = definitions["Collection"]

/**
 * Formik form values structure for collections creation.
 */
interface CollectionsFormik {
  /** Array of collections to be created */
  collections: Collection[]
}

const validationSchema = Yup.object({
  collections: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().required("Collection name is required"),
        branches_ids: Yup.array().min(1, "At least one branch is required").required("At least one branch is required"),
      })
    )
    .min(1, "At least one collection is required")
    .required("At least one collection is required"),
})

/**
 * Props for the AddNewCollectionDialog component.
 */
interface AddNewCollectionDialogProps {
  /** Callback invoked when the dialog should be closed */
  onClose: () => void
  /** Whether the dialog is currently open */
  isOpen: boolean
  /** Array of available branches that can be added to collections */
  branches?: Branch[]
}

/**
 * AddNewCollectionDialog component provides a dialog interface for creating new collections.
 *
 * Features:
 * - Create one or multiple collections in a single form
 * - Each collection requires a name and at least one branch
 * - Optimized branch availability calculation (prevents duplicate selections)
 * - Form validation using Yup schema
 * - Creates all collections in parallel when submitted
 * - Automatically updates available branches as selections change
 *
 * @param props - The component props
 * @returns The rendered dialog component
 */
const AddNewCollectionDialog: FC<AddNewCollectionDialogProps> = ({ onClose, isOpen, branches }) => {
  const queryClient = useQueryClient()

  /**
   * Handles dialog close by resetting the form and calling the onClose callback.
   */
  const handleCloseDialog = () => {
    formik.resetForm()
    onClose()
  }

  /**
   * Mutation hook for creating collections.
   * Creates all collections in parallel and invalidates related queries on success.
   */
  const { mutate: createCollections, isLoading: isCreating } = useMutation(
    async (collections: Collection[]) => {
      // Create all collections in parallel
      return Promise.all(
        collections.map((collection) =>
          VisionAPI.createCollection({
            name: collection.name,
            branches_ids: collection.branches_ids,
          })
        )
      )
    },
    {
      onSuccess: () => {
        NotificationUtils.toast("Collections created successfully", {
          severity: "success",
        })
        queryClient.invalidateQueries("fetchCollections")
        queryClient.invalidateQueries("fetchUncollectedBranches")
        handleCloseDialog()
      },
      onError: (error: any) => {
        NotificationUtils.toast(`Failed to create collections: ${error?.message || "Unknown error"}`, {
          severity: "error",
        })
      },
    }
  )

  const formik = useFormik<CollectionsFormik>({
    initialValues: {
      collections: [{ name: "", branches_ids: [] }],
    },
    validationSchema,
    onSubmit: (values) => createCollections(values.collections),
  })

  /**
   * Adds a new empty collection to the form.
   */
  const handleAddCollection = () => {
    formik.setFieldValue("collections", [...formik.values.collections, { name: "", branches_ids: [] }])
  }

  /**
   * Removes a collection from the form by index.
   *
   * @param index - The index of the collection to remove
   */
  const handleDeleteCollection = (index: number) => {
    const updatedCollections = formik.values.collections.filter((_, i) => i !== index)
    formik.setFieldValue("collections", updatedCollections)
  }

  /**
   * Memoized available branches for each collection index.
   * Optimized to O(collections + branches) instead of O(collections * branches).
   *
   * For each collection, returns branches that are either:
   * 1. Not selected in any collection, OR
   * 2. Selected in the current collection (to keep them visible)
   */
  const availableBranchesByIndex = useMemo(() => {
    if (!branches) return []

    const collectionsCount = formik.values.collections.length
    const result: Branch[][] = Array.from({ length: collectionsCount }, () => [])

    // Build a map: branchId -> Set of collection indices that selected it (single pass)
    const branchToCollections = new Map<number, Set<number>>()
    formik.values.collections.forEach((collection, index) => {
      collection.branches_ids?.forEach((branchId) => {
        if (!branchToCollections.has(branchId)) {
          branchToCollections.set(branchId, new Set())
        }
        branchToCollections.get(branchId)!.add(index)
      })
    })

    // Single pass through branches: assign each branch to appropriate collections
    branches.forEach((branch) => {
      const branchId = branch.id!
      const selectedInCollections = branchToCollections.get(branchId) || new Set<number>()

      if (selectedInCollections.size === 0) {
        // Branch not selected in any collection - available to all
        for (let i = 0; i < collectionsCount; i++) {
          result[i].push(branch)
        }
      } else {
        // Branch selected in some collections - only available to those collections
        selectedInCollections.forEach((collectionIndex) => {
          result[collectionIndex].push(branch)
        })
      }
    })

    return result
  }, [branches, formik.values.collections])

  /**
   * Helper function to extract error messages for collection form fields.
   *
   * @param index - The index of the collection in the form
   * @param field - The field name to get the error for ("name" or "branches_ids")
   * @returns The error message string, or undefined if no error
   */
  const getCollectionError = (index: number, field: "name" | "branches_ids"): string | undefined => {
    const collectionError = formik.errors.collections?.[index]
    if (
      collectionError &&
      typeof collectionError === "object" &&
      !Array.isArray(collectionError) &&
      field in collectionError
    ) {
      return String((collectionError as { [key: string]: string | string[] })[field])
    }
    return undefined
  }

  return (
    <VisionDialog
      title="Add New Collection"
      isOpen={isOpen}
      onClose={handleCloseDialog}
      customActions={
        <div className={styles.actionButton}>
          <Button
            variant="primary"
            onClick={() => formik.handleSubmit()}
            disabled={!formik.isValid || !formik.dirty || isCreating}
            isLoading={isCreating}
            tooltip={!formik.isValid ? "All required fields must be filled" : ""}
          >
            Save
          </Button>
        </div>
      }
      hasStickyHeader
    >
      <div className={styles.addNewCollectionContainer}>
        {formik.values.collections.map((collection, index) => (
          <div key={index} className={styles.addNewCollectionContentItem}>
            <InputText
              label="Collection Name"
              value={collection.name}
              handleChange={(event) => formik.setFieldValue(`collections.${index}.name`, event.target.value)}
              handleBlur={() => formik.setFieldTouched(`collections.${index}.name`, true)}
              error={formik.touched.collections?.[index]?.name ? getCollectionError(index, "name") : undefined}
              fullWidth
              required
            />
            <BranchesAutocomplete
              selectedValues={collection.branches_ids || []}
              onSelectionChange={(selectedBranches) =>
                formik.setFieldValue(`collections.${index}.branches_ids`, selectedBranches)
              }
              handleBlur={() => formik.setFieldTouched(`collections.${index}.branches_ids`, true)}
              branches={availableBranchesByIndex[index] || []}
              label="Branches"
              fullWidth
              required
              hideDescription={false}
              error={
                formik.touched.collections?.[index]?.branches_ids && collection.branches_ids?.length === 0
                  ? getCollectionError(index, "branches_ids")
                  : undefined
              }
            />
            <Button
              variant="secondary"
              startIcon={<DeleteIcon className={styles.icon} />}
              disabled={formik.values.collections.length === 1}
              onClick={() => handleDeleteCollection(index)}
              size="small"
            >
              Delete this Collection
            </Button>
          </div>
        ))}
        <Button
          startIcon={<AddCircleOutlineIcon sx={{ fontSize: "14px" }} />}
          size="small"
          onClick={handleAddCollection}
        >
          Add another collection
        </Button>
      </div>
    </VisionDialog>
  )
}
export default AddNewCollectionDialog
